// Notifications feed for vendors (uses the shared /api/notifications endpoint).
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscribe } from "@/contexts/RealtimeContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Bell, CheckCircle2, Trophy, XCircle, HardHat, DollarSign } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  rfp_invite: { icon: HardHat, tone: "text-indigo-300 border-indigo-500/40 bg-indigo-500/10", label: "RFP Invite" },
  rfp_awarded: { icon: Trophy, tone: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10", label: "RFP Won" },
  rfp_lost: { icon: XCircle, tone: "text-zinc-400 border-zinc-500/40 bg-zinc-500/10", label: "RFP Closed" },
  bid_accepted: { icon: DollarSign, tone: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10", label: "Bid Accepted" },
};

export default function VendorNotificationsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    const { data } = await api.get("/notifications");
    setItems(data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Live: new notifications flow in without a refresh.
  useSubscribe(user ? `user:${user.id}` : null, ({ event, payload }) => {
    if (event === "notification.new") {
      // Optimistic prepend; server-side records already persisted.
      setItems((prev) => [{
        id: payload.linked_id ? `live-${payload.linked_id}-${Date.now()}` : `live-${Date.now()}`,
        read: false,
        created_at: new Date().toISOString(),
        ...payload,
      }, ...prev]);
    }
  });

  const markRead = async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setItems((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
    } catch {
      toast.error("Couldn't mark as read");
    }
  };

  const unreadCount = items.filter((n) => !n.read).length;

  return (
    <div>
      <PageHeader
        eyebrow="Feed"
        title="Notifications"
        subtitle="Every RFP invite, bid decision, and job update — in one stream."
        actions={
          <Badge variant="outline" className="border-white/10">
            <Bell className="h-3 w-3 mr-1" /> {unreadCount} unread
          </Badge>
        }
      />
      <div className="space-y-2" data-testid="vendor-notifications-list">
        {items.map((n) => {
          const meta = TYPE_META[n.type] || { icon: Bell, tone: "text-zinc-300 border-white/10 bg-white/[0.03]", label: n.type };
          const Icon = meta.icon;
          return (
            <Card
              key={n.id}
              className={"border-white/10 bg-card p-4 " + (n.read ? "opacity-70" : "")}
              data-testid={`vnotif-${n.id}`}
            >
              <div className="flex items-start gap-3">
                <div className={"h-9 w-9 rounded-md flex items-center justify-center border " + meta.tone}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium truncate">{n.title}</div>
                    <Badge variant="outline" className={"border-white/10 " + (n.read ? "" : "text-orange-300")}>
                      {meta.label}
                    </Badge>
                    {!n.read && <span className="text-[10px] uppercase tracking-widest text-orange-300">New</span>}
                  </div>
                  <div className="text-sm text-zinc-300 mt-0.5">{n.body}</div>
                  <div className="text-xs text-zinc-500 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                </div>
                {!n.read && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs text-zinc-400 hover:text-white shrink-0"
                    onClick={() => markRead(n.id)}
                    data-testid={`vnotif-read-${n.id}`}
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark read
                  </Button>
                )}
              </div>
            </Card>
          );
        })}
        {items.length === 0 && (
          <Card className="border-dashed border-white/10 p-10 text-center text-muted-foreground">
            No notifications yet. Invitations, awards, and updates will land here.
          </Card>
        )}
      </div>
    </div>
  );
}
